import React from 'react';
import { withRouter } from 'react-router-dom';
import { Howl } from 'howler';
import ChatBot from '../ChatBot/ChatBot';
import { genSpeechGestures, amiraGestures } from './GesturedSpeech';
import { spine } from "./../../spine-webgl";
import { gameSFXKeys } from '../../constants/constants';
import { DANCE_TITLES } from "../../services/badging/constants";
import { getUserData } from "../../services/API";
import { logSentryException, shouldForceHTML5Audio } from '../../services/util';
import * as Sentry from '@sentry/browser';
import { GetCharacterConfiguration } from '../../services/CharacterService';
// Rig positions/scales live in the shared avatar-layout module; Coach also publishes the
// pose it applies (setAvatarPose) so the TT-1166 thinking bubble anchors to the exact
// coordinates drawn here without re-deriving (and drifting from) them.
import { AVATAR_POSITIONS as POSITIONS, AVATAR_SCALE as SCALE, setAvatarPose } from '../../services/avatarLayout';
import { logCorruptedSpeechString, logSessionInfo } from '../../services/logger';
import { globalContext, prepareMicForPlayback, prepareMicForRecord } from '../audiocapture/recorder';
import { isiPad } from '../../services/environment';
import StaticAmiraFallback from './StaticAmiraFallback';

/** home app section */
// songs for dances
const funkyChickenSong = require('./assets/sounds/dances/Chicken.wav');
const flossSong = require('./assets/sounds/dances/Floss.wav');
const renegadeSong = require('./assets/sounds/dances/Renedgade.wav');
/** home app section end */

/** home app section */
var animationJson;
var visemeArray = [];
let gestureArray = [];
/** home app section end */

let speaking = false;
let speechTimeout = null;

// Track rendering state to prevent concurrent loads
let amiraRenderingState = {
  isLoading: false,
  loadingPromise: null,
  loadedAtlas: null,
  loadedSkeleton: null,
  lastLoadTimestamp: null,
  retryCount: 0,
  maxRetries: 2 // Retry twice before showing fallback
};

/** home app section */
let media = null;
let speechPropsArray = [];
let mounted = false;

let timer;  //for readaloud timer
let time;   //for readaloud time within the timer to reset between sentences
let manualStopTimer; // for catching instances where status === 4 is not called
let mediaDurationTimer; // to prevent androids from not being able to get duration
let audioTimeoutId = null; // timeout safeguard for Howl playback/loading

let amiraAvatar = {
  name: 'amira',
  scale: getUserData().sessionData.isPhone ? new spine.Vector2(0.9, 0.9) : new spine.Vector2(0.51, 0.51),
  spineContent: null,
  pos: null,
};

export const DANCES = [
  {
    title: DANCE_TITLES.CHICKEN,
    audioUrl: funkyChickenSong,
    gesture: amiraGestures.TheFunkyChicken,
  },
  {
    audioUrl: "renegadeSong",
    gesture: amiraGestures.TheRenegadeDance,
  },
  {
    title: DANCE_TITLES.RENEGADE,
    audioUrl: renegadeSong,
    gesture: amiraGestures.TheRenegadeDance,
  },
  {
    title: DANCE_TITLES.FLOSS,
    audioUrl: flossSong,
    gesture: amiraGestures.TheFlossDance,
  },
];
/** home app section end */

class Coach extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      source: "Lexa",
      postMessageData: "Init",
      passThroughSpeech: false,         //If this name changes, must change Sumerian too (so don't do it)
      audioURL: null,
      isHidden: false, // home app
    };

    // Track mounted state to prevent memory leaks from event listeners
    this._isMounted = false;

    amiraAvatar.scale = new spine.Vector2(window.innerHeight/1000, window.innerHeight/1000); // home app

    this.speak = this.speak.bind(this);
    this.onReceiveMessage = this.onReceiveMessage.bind(this);
    this.onAbortPlaying = this.onAbortPlaying.bind(this);
    this.onFinishedPlaying = this.onFinishedPlaying.bind(this);
    this.speechEndCallback = this.speechEndCallback.bind(this);
    this.speechStartCallback = this.speechStartCallback.bind(this);
    this.abortSpeech = this.abortSpeech.bind(this);
    /** home app secontion */
    this.onUtteranceStart = this.onUtteranceStart.bind(this);
    this.onWordEnd = this.onWordEnd.bind(this);
    this.onSpeaking = this.onSpeaking.bind(this);
    this.playSpeech = this.playSpeech.bind(this);

    /** home app secontion end */
    this.renderAmira = this.renderAmira.bind(this);
  }

  speechEndCallback(speechObj) {
    //LEAVE IN FOR DEBUG
    //console.log("ON SPEECH END FIRED:", Date.now());
    if (speechObj && speechObj.onEnd && mounted) {
      speechObj.onEnd();
    }
  }

  speechStartCallback(speechObj) {
    if (speechObj.onStart) speechObj.onStart();
    speaking = true;
    speechTimeout && window.clearTimeout(speechTimeout);
  }

  registerAbortHandler(prevProps) {
    if (prevProps.speech && !prevProps.speech.setAbortSpeech && this.props.speech.setAbortSpeech) {
      this.props.speech.setAbortSpeech(this.abortSpeech);
    }
    if (prevProps.speech && !prevProps.speech.setAbortDance && this.props.speech.setAbortDance) {
      this.props.speech.setAbortDance(this.abortDance);
    }
  }

  setupEventListeners() {
    window.addEventListener('webgl-context-restored-complete', this.handleContextRestored);
    window.addEventListener('webgl-texture-restoration-errors', this.handleTextureRestorationErrors);
    window.addEventListener('atlas-validation-errors', this.handleAtlasValidationErrors);
    window.addEventListener('texture-dimension-mismatch', this.handleTextureDimensionMismatch);
    window.addEventListener('texture-load-error', this.handleTextureLoadError);
    window.addEventListener('amira-invalid-slot-textures', this.handleInvalidSlotTextures);
    window.addEventListener('atlas-pages-invalid', this.handleAtlasPagesInvalid);
    
    // Listen for test trigger (only in dev mode)
    if (process.env.NODE_ENV !== 'production') {
      window.addEventListener('test-trigger-amira-render', this.handleTestTriggerRender);
    }
  }

  removeEventListeners() {
    // Safe to call multiple times - removeEventListener is idempotent
    try {
      window.removeEventListener('webgl-context-restored-complete', this.handleContextRestored);
      window.removeEventListener('webgl-texture-restoration-errors', this.handleTextureRestorationErrors);
      window.removeEventListener('atlas-validation-errors', this.handleAtlasValidationErrors);
      window.removeEventListener('texture-dimension-mismatch', this.handleTextureDimensionMismatch);
      window.removeEventListener('texture-load-error', this.handleTextureLoadError);
      window.removeEventListener('amira-invalid-slot-textures', this.handleInvalidSlotTextures);
      window.removeEventListener('atlas-pages-invalid', this.handleAtlasPagesInvalid);
      
      if (process.env.NODE_ENV !== 'production') {
        window.removeEventListener('test-trigger-amira-render', this.handleTestTriggerRender);
      }
    } catch (e) {
      console.warn('Error removing event listeners:', e);
    }
  }

  /** home app section */
  componentDidMount() {
    mounted = true;
    this._isMounted = true;
    this.props.speech.setAbortSpeech(this.abortSpeech.bind(this));
    this.props.speech.setAbortDance(this.abortDance.bind(this));
    this.props.setCoachReady(true);
    this.setState({isHidden: this.props.isHidden});
    console.log("COACH", this.props.history)

    // Setup event listeners for texture/WebGL events
    this.setupEventListeners();

    // Check for WebGL error and skip Spine rendering if WebGL failed
    if (!this.props.webglError) {
      if(!this.props.isHidden){
        this.renderAmira()
      }else{
        this.props.webglProps.stopAnimation()
      }
    }
  }

  /** home app section end */
  renderAmira(){
    // Skip rendering if WebGL failed
    if (this.props.webglError) {
      return;
    }

    // Prevent concurrent rendering calls
    if (amiraRenderingState.isLoading) {
      // Return existing promise if available
      if (amiraRenderingState.loadingPromise) {
        return amiraRenderingState.loadingPromise;
      }
      return;
    }

    amiraRenderingState.isLoading = true;
    amiraRenderingState.lastLoadTimestamp = Date.now();

    const characterConfig = GetCharacterConfiguration(getUserData());
    const rigName = characterConfig.rig;
    const skeletonPath = characterConfig.cdnSkeletonURL;
    const atlasPath = characterConfig.cdnAtlasURL;
    const texturePaths = characterConfig.cdnTextureURLs;

    const realToCSSPixels = window.devicePixelRatio;
    const onSkeletonLoaded = new Promise((resolve, reject) => {
      // Check for test flag to simulate render error
      if (window._testForceRenderError) {
        reject(new Error('TEST: Simulated render error'));
        return;
      }
      
      try {
        this.props.webglProps.loadSkeletonData(
          skeletonPath, 
          atlasPath, 
          texturePaths, 
          (skeletonData) => {
            // Validate skeleton data
            if (!skeletonData || !skeletonData.bones || skeletonData.bones.length === 0) {
              console.error('Invalid skeleton data received');
              reject(new Error('Invalid skeleton data'));
              return;
            }
            resolve(skeletonData);
          }
        );
      } catch (error) {
        console.error('Error calling loadSkeletonData', error);
        reject(error);
      }
    });
    
    // Store promise for potential reuse
    amiraRenderingState.loadingPromise = onSkeletonLoaded;
    
    onSkeletonLoaded.then((amiraSkeletonData) => {
      // Double-check WebGL error state in case it changed during loading
      if (this.props.webglError) {
        amiraRenderingState.isLoading = false;
        amiraRenderingState.loadingPromise = null;
        return;
      }
      
      amiraRenderingState.loadedSkeleton = amiraSkeletonData;
      amiraAvatar.spineContent = this.props.webglProps.createSpineInstance(amiraSkeletonData, "visemes/viseme_sil", "amira", characterConfig.skin);

      // Validate spine instance creation
      if (!amiraAvatar.spineContent || !amiraAvatar.spineContent.skeleton) {
        console.error('Failed to create spine instance');
        amiraRenderingState.isLoading = false;
        amiraRenderingState.loadingPromise = null;
        return;
      }

      // Validate and apply skin
      const tutorSkin = amiraAvatar.spineContent.skeleton.data.findSkin(characterConfig.skin);
      if (!tutorSkin) {
        console.error('Requested skin not found:', characterConfig.skin);
        amiraRenderingState.isLoading = false;
        amiraRenderingState.loadingPromise = null;
        return;
      }
      
      amiraAvatar.spineContent.skeleton.setSkin(tutorSkin);
      amiraAvatar.spineContent.skeleton.setSlotsToSetupPose();
      
      // Validate slot textures (minimal logging)
      this.validateSlotTextures(amiraAvatar.spineContent.skeleton);

      let scale
      if (rigName === 'Amira2') {
        scale = SCALE.NEW_RIG;
      } else {
        scale = SCALE.DEFAULT;
      }

      const aspect = window.innerHeight / window.innerWidth
      const isiPadRatio = aspect >= 0.62
      const isLowResScreen = realToCSSPixels < 2
      const isSmallScreen = window.innerWidth <= 1300
    
      // this positioning parameters are fixed values because the canvas is a fixed size.
      let posX = POSITIONS.DEFAULT.X;
      let posY = POSITIONS.DEFAULT.Y;

      if (rigName === 'Amira2') {
        posX = POSITIONS.NEW_RIG_DEFAULT.X;
				posY = POSITIONS.NEW_RIG_DEFAULT.Y;
			}
      
      // Adjust position for small screens to prevent blocking text
      if (isSmallScreen) {
        if (rigName === 'Amira2') {
          posX = POSITIONS.NEW_RIG_SMALL_SCREEN.X;
          posY = POSITIONS.NEW_RIG_SMALL_SCREEN.Y;
        } else {
          posX = POSITIONS.SMALL_SCREEN.X;
          posY = POSITIONS.SMALL_SCREEN.Y;
        }
      }
      
      if(!isiPadRatio){
        // adjust the Y position for Chromebook zoomed settings.
        if(isLowResScreen) {
          posY = posY / realToCSSPixels

					if (rigName === 'Amira2'){
						posY += 150;
					}
        }
      } 

    amiraAvatar.pos = new spine.Vector2(posX, posY);
    amiraAvatar.scale = new spine.Vector2(scale, scale);
    setAvatarPose(posX, posY); // publish for the TT-1166 thinking bubble

    if (!this.props.isHidden && !this.props.webglError) {
      this.props.webglProps.sendSpineRenderable(amiraAvatar);
    }
    
    // Mark loading complete and reset retry count on success
    amiraRenderingState.isLoading = false;
    amiraRenderingState.loadingPromise = null;
    amiraRenderingState.retryCount = 0;
    
    // Note: webglError remains true if it was set - persistent failure requires refresh
    
  }).catch(error => {
    console.error('Error rendering amira:', error);
    logSessionInfo('amira_render_error', {
      error: error,
      stack: error.stack,
      timestamp: Date.now(),
      retryCount: amiraRenderingState.retryCount
    });
    
    // Reset loading state
    amiraRenderingState.isLoading = false;
    amiraRenderingState.loadingPromise = null;
    
    // Implement retry with exponential backoff
    if (amiraRenderingState.retryCount < amiraRenderingState.maxRetries) {
      amiraRenderingState.retryCount++;
      const retryDelay = Math.pow(2, amiraRenderingState.retryCount) * 1000; // 2s, 4s
      
      setTimeout(() => {
        if (!this.props.webglError && !this.props.isHidden) {
          this.renderAmira();
        }
      }, retryDelay);
    } else {
      // Max retries exceeded - show static fallback
      console.error('Max retries exceeded for Amira rendering, showing static fallback');
      
      // Remove Amira from WebGL renderer and clear canvas (canvas stays visible but empty)
      if (!this.props.webglError) {
        this.props.webglProps.removeSpineRenderable(amiraAvatar);
        
        // Clear canvas after removal - canvas stays visible but renders nothing
        requestAnimationFrame(() => {
          const canvas = document.getElementById('canvas');
          const gl = canvas && canvas.getContext('webgl');
          if (gl && !gl.isContextLost()) {
            try {
              gl.clearColor(0, 0, 0, 0);
              gl.clear(gl.COLOR_BUFFER_BIT);
            } catch (e) {
              console.warn('Could not clear canvas:', e);
            }
          }
        });
      }
      
      // Signal to WebGLContext that rendering has failed - sets webglError=true
      // This makes the app treat it like initialization failure (rightPanel gets webgl-error class)
      // webglError is the single source of truth for error state
      if (!this.props.webglError && this.props.webglProps.setRenderError) {
        this.props.webglProps.setRenderError(true);
      }
      
      amiraRenderingState.retryCount = 0;
    }
  });
}

  validateSlotTextures(skeleton) {
    const slots = skeleton.slots;
    let invalidSlots = [];
    
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      const attachment = slot.getAttachment();
      
      if (attachment && attachment.region) {
        const region = attachment.region;
        
        // Validate region has texture with image
        if (!region.texture || !region.texture._image) {
          console.error('Slot missing valid texture:', slot.data.name, attachment.name);
          invalidSlots.push({
            slotName: slot.data.name,
            attachmentName: attachment.name,
            error: 'No texture or image'
          });
        }
      }
    }
    
    if (invalidSlots.length > 0) {
      console.error('Invalid textures found:', invalidSlots.length, 'slots');
      window.dispatchEvent(new CustomEvent('amira-invalid-slot-textures', {
        detail: {
          invalidSlots: invalidSlots,
          timestamp: Date.now()
        }
      }));
    }
    
    return invalidSlots;
  }

  adjustAmiraToOnScreenKeyboardPosition() {

    if (this.props.webglError) {
      return;
    }

    const realToCSSPixels = window.devicePixelRatio;
    const isLowResScreen = realToCSSPixels < 2
    const aspect = window.innerHeight / window.innerWidth
    const isiPadRatio = aspect >= 0.62

    // this positioning parameters are fixed values because the canvas is a fixed size.
    let posX = POSITIONS.ON_SCREEN_KEYBOARD.X
    let posY = POSITIONS.ON_SCREEN_KEYBOARD.Y
    const characterConfig = GetCharacterConfiguration(getUserData());
    const rigName = characterConfig.rig;
    if (rigName === 'Amira2') {
      posX = POSITIONS.NEW_RIG_DEFAULT.X;
      posY = POSITIONS.NEW_RIG_DEFAULT.Y;
    }

    if(!isiPadRatio){
      // adjust the Y position for Chromebook zoomed settings.
      if(isLowResScreen) {
        posY = posY / realToCSSPixels
      }
    }

    amiraAvatar.pos = new spine.Vector2(posX, posY);
    setAvatarPose(posX, posY); // publish for the TT-1166 thinking bubble
  }

  adjustAmiraToDefaultPosition() {

    if (this.props.webglError) {
      return;
    }

    const realToCSSPixels = window.devicePixelRatio;
    const isLowResScreen = realToCSSPixels < 2
    const aspect = window.innerHeight / window.innerWidth
    const isiPadRatio = aspect >= 0.62
    const isSmallScreen = window.innerWidth <= 1300

    // this positioning parameters are fixed values because the canvas is a fixed size.
    let posX = POSITIONS.DEFAULT.X
    let posY = POSITIONS.DEFAULT.Y

    const characterConfig = GetCharacterConfiguration(getUserData());
    const rigName = characterConfig.rig;
    if (rigName === 'Amira2') {
      posX = POSITIONS.NEW_RIG_DEFAULT.X;
      posY = POSITIONS.NEW_RIG_DEFAULT.Y;
    }

    // Adjust position for small screens to prevent blocking text
    if (isSmallScreen) {
      if (rigName === 'Amira2') {
        posX = POSITIONS.NEW_RIG_SMALL_SCREEN.X;
        posY = POSITIONS.NEW_RIG_SMALL_SCREEN.Y;
      } else {
        posX = POSITIONS.SMALL_SCREEN.X;
        posY = POSITIONS.SMALL_SCREEN.Y;
      }
    }

    if(!isiPadRatio){
      // adjust the Y position for Chromebook zoomed settings.
      if(isLowResScreen) {
        posY = posY / realToCSSPixels
      }
    }

    amiraAvatar.pos = new spine.Vector2(posX, posY);
    setAvatarPose(posX, posY); // publish for the TT-1166 thinking bubble
  }


  componentDidUpdate(prevProps, prevState) {
    this.registerAbortHandler(prevProps);

    // If the isHidden prop has changed
    if(this.props.isHidden !== this.state.isHidden) {
      if (!this.props.webglError) {
        // If it is changing from true to false, reanimate amira
        if (!this.props.isHidden && this.state.isHidden) {
          visemeArray = [];
          gestureArray = [];
          this.props.webglProps.sendAmiraVisemeArray(visemeArray);
          this.props.webglProps.sendAmiraGestureArray(gestureArray);
          this.props.webglProps.sendSpineRenderable(amiraAvatar);
          console.log("componentDidUpdate Coach", prevProps)
          this.props.webglProps.moveWebglLayer('highestZ');
        } else {
          this.props.webglProps.removeSpineRenderable(amiraAvatar);
        }
      }
      // Update state
      this.setState({isHidden: this.props.isHidden});
    }

    // TODO: Move in the functionality of speaking from LexaSpeech to here
    if (this.props.message.id !== prevProps.message.id) {
      if ((this.props.message.skipAvatar) || (this.props.config.useSumerian == false)) {
        this.speak(this.props.message, true);
      } else {
        this.speak(this.props.message, false);
      }
    }

    // If the coach has received new speech marks and audio to say
    if (this.props.avatarSpeechProps.speech_marks) {
      if (!animationJson || this.props.avatarSpeechProps.id !== prevProps.avatarSpeechProps.id) {
        // Set the animation json and load the visemes and gestures
        animationJson = this.props.avatarSpeechProps.speech_marks;
        this.playSpeech(this.props.avatarSpeechProps);
      }
    }

    if(this.props.avatarSpeechProps.dance
        &&  (!prevProps.avatarSpeechProps.dance
            || (this.props.avatarSpeechProps.dance.gesture != prevProps.avatarSpeechProps.dance.gesture))) {
      this.playDance();
    }

    if (((!prevProps.avatarSpeechProps && this.props.avatarSpeechProps) || (this.props.avatarSpeechProps.id != prevProps.avatarSpeechProps.id)) && (this.props.config.useSumerian == true)) {
      let passThrough = false;
      if (this.props.avatarSpeechProps.passThroughSpeechTags) {
        passThrough = true;
      }
    }

    const wasOskOpen = prevProps.avatarSpeechProps?.oskOpen;
    const isOskOpen = this.props.avatarSpeechProps?.oskOpen;

    if (wasOskOpen !== isOskOpen) {
      if (isOskOpen) {
        this.adjustAmiraToOnScreenKeyboardPosition();
      } else {
        this.adjustAmiraToDefaultPosition();
      }
    }
  }
  

  /** home app section */
  componentWillUnmount() {
    // Set mounted state before cleanup to prevent handlers from executing
    mounted = false;
    this._isMounted = false;
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    if (mediaDurationTimer) {
      clearTimeout(mediaDurationTimer);
      mediaDurationTimer = null;
    }
    if (manualStopTimer) {
      clearTimeout(manualStopTimer);
      manualStopTimer = null;
    }
    if (audioTimeoutId) {
      clearTimeout(audioTimeoutId);
      audioTimeoutId = null;
    }
    if (media) {
      this.destroyMedia();
      this.onFinishedPlaying();
    }

    // Only call WebGL methods if WebGL is working
    if (!this.props.webglError) {
      this.props.webglProps.removeSpineRenderable(amiraAvatar);
    }

    // Remove all event listeners to prevent memory leaks
    this.removeEventListeners();

    animationJson = null;
    visemeArray = [];
    gestureArray = [];

    this.abortSpeech();
  }
  /** home app section end */

  destroyMedia(){
    // Clear the audio timeout since we're destroying the media
    // This prevents the timeout from firing after media cleanup
    if (audioTimeoutId) {
      clearTimeout(audioTimeoutId);
      audioTimeoutId = null;
    }
    
    if (media) {
      media.off();
      media.stop();
      media.unload();
      media = null;
    }
  }

  /** home app section */
  async playSpeech(nextSpeech) {
    // Skip WebGL-related operations if WebGL failed
    if (!this.props.webglError) {
      // Prepare animations
      this.loadVisemes();
      this.loadGestures();
    }

    if(nextSpeech.active) return;

    nextSpeech.active = true;
    speechPropsArray.push(nextSpeech);

    time = Date.now();
    
    const setOnSpeakingTimer = (audioStopped = false) => {
      if (timer) {
        clearInterval(timer);
      }
      timer = setInterval(() => {
        let now = Date.now();
        let timeAfter = now - time;

        if (audioStopped) {
          time = Date.now();
          clearInterval(timer);
        }

        this.onSpeaking(timeAfter)
      }, 32)
    }

    // Start the audio
    const playMedia = speechObj => {
      if (speechObj && !speechObj.aborted) {
        speaking = true;
        if (!this.props.webglError) {
          this.props.webglProps.sendAmiraVisemeArray(visemeArray);
          this.props.webglProps.sendAmiraGestureArray(gestureArray);
        }
        speechObj.media.play();
      }
    }

    // On audio play callback
    const onPlay = speechObj => {
      console.log('playing speech... onPlay', speechObj.textToSpeak || speechObj.audioURL);
      if (!this.props.webglError) {
        this.props.webglProps.resetAmiraAnimations();
        this.props.webglProps.setAmiraPlayAnimations(true);
      }
      speechObj.playing = true;
      this.onUtteranceStart(speechObj);
    }

    // On audio end callback
    const onEnd = speechObj => {
      console.log('playing speech... onEnd', speechObj.textToSpeak || speechObj.audioURL);
      speaking = false;
      speechObj.playing = false;
      if(!speechObj.aborted){
        if (!this.props.webglError) {
          this.props.webglProps.sendAmiraVisemeArray([]);
        }
        this.destroyMedia();
        prepareMicForRecord();
        this.props.speech.restartRecorderStreamIfNeeded();
        this.onFinishedPlaying(speechObj);
      }
    }

    // Audio audio error callback
    const onError = (error, speechObj) => {
      console.error('Error coach:', error);
      speaking = false;
      speechObj.playing = false;
      if(!speechObj.aborted){
        if (!this.props.webglError) {
          this.props.webglProps.sendAmiraVisemeArray([]);
        }
        this.destroyMedia();
        prepareMicForRecord();
        this.props.speech.restartRecorderStreamIfNeeded();
        this.onFinishedPlaying(speechObj);
      }
      // This is to allow speech caller to handle errors raised the audio player without relying on the onEnd to be called.
      if(speechObj.onError) {
        speechObj.onError();
      }
    }

    // Set up a timeout safeguard to ensure onEnd is called even if Howl fails
    // This prevents infinite hangs when audio loading or playback fails silently
    // Start with a conservative initial timeout for the loading phase (60s)
    // Once audio loads, we'll reset based on actual audio duration
    const INITIAL_LOAD_TIMEOUT_MS = 10000; // 10 seconds for loading
    const PLAYBACK_BUFFER_MS = 5000; // 5 second buffer after audio duration
    
    let callbackFired = false;
    
    const ensureCallbackOnce = (callback) => {
      return (...args) => {
        if (callbackFired) {
          console.warn('⚠️ Audio callback already fired, preventing duplicate call');
          return;
        }
        callbackFired = true;
        if (audioTimeoutId) {
          clearTimeout(audioTimeoutId);
          audioTimeoutId = null;
        }
        callback(...args);
      };
    };
    
    const safeOnError = ensureCallbackOnce(onError);
    const safeOnEnd = ensureCallbackOnce(onEnd);
    
    const setTimeoutForDuration = (timeoutMs, phase = 'unknown') => {
      if (audioTimeoutId) {
        clearTimeout(audioTimeoutId);
      }
      
      audioTimeoutId = setTimeout(() => {
        console.error(`❌ Audio ${phase} timeout after`, timeoutMs, 'ms');
        logSessionInfo('coach_audio_timeout', {
          audioURL: this.props.avatarSpeechProps?.audioURL,
          speechText: this.props.avatarSpeechProps?.speechText,
          timeoutMs,
          phase,
        });
        
        if (media) {
          try {
            media.unload();
          } catch (unloadError) {
            console.error('Error unloading media on timeout:', unloadError);
          }
        }
        
        safeOnError(new Error(`Audio ${phase} timeout`), nextSpeech);
      }, timeoutMs);
    };
    
    // Set initial timeout for loading phase
    setTimeoutForDuration(INITIAL_LOAD_TIMEOUT_MS, 'loading');
    
    // Track if we've already attempted the unlock flow to prevent infinite loops
    // If playMedia fails after unlock, we should fail immediately rather than waiting again
    let unlockAttempted = false;

    try{
      if(media){
        this.destroyMedia();
      }

      prepareMicForPlayback();
      this.props.speech.cancelRecorderStreamIfNeeded();
      
      const hasMp3Support = Howler.codecs('mp3');

      media = new Howl({
        src: [this.props.avatarSpeechProps.audioURL],
        format: ['mp3'],
        volume: 1,
        pool: 1,
        onloaderror: (id, error) => {
          console.error('Sound on load error:', error);
          console.error('Error details:', JSON.stringify({
            text: this.props.avatarSpeechProps.speechText,
            audioURL: this.props.avatarSpeechProps.audioURL,
            globalContextState: globalContext?.state,
            hasMp3Support,
          }));
          logCorruptedSpeechString(this.props.avatarSpeechProps.speechText);
          logSentryException(new Error('Coach sound load failure'), 'Sound on load error', { originalError: error?.message, originalStack: error?.stack });
          safeOnError(error, nextSpeech);
        },
        onplayerror: (id, error) => {
          console.error('Sound on play error');
          // Log error details for debugging - Howler.js doesn't provide standardized error codes
          console.error('Error details:', JSON.stringify({
            code: error?.code,
            message: error?.message,
            name: error?.name,
            type: typeof error
          }, null, 2));
          logSentryException(new Error(`Speech onPlayError: ${error?.message || 'unknown'}`));

          // Attempt unlock flow once to handle potential autoplay restrictions
          // Howler.js does not provide standardized error codes, so we can't reliably
          // distinguish autoplay errors from other errors. We attempt unlock once
          // for any play error, but prevent infinite loops with unlockAttempted flag.
          if (!unlockAttempted) {
            console.log('Play error occurred, attempting unlock flow (may be autoplay restriction)');
            unlockAttempted = true;
            
            // Set a timeout for the unlock attempt
            const unlockTimeoutId = setTimeout(() => {
              console.error('Audio unlock timeout - calling onError');
              // Clean up the unlock listener to prevent it from firing after timeout
              if (media) {
                media.off('unlock');
              }
              safeOnError(new Error('Audio unlock timeout'), nextSpeech);
            }, 3000);
            
            if(media){
              media.once('unlock', function() {
                clearTimeout(unlockTimeoutId);
                console.log('Audio unlocked, attempting to play');
                
                // Validate media still exists and is in a valid state
                // Media might have been destroyed by the timeout or another error path
                if (!media || media.state() === 'unloaded') {
                  console.warn('Media no longer available after unlock');
                  safeOnError(new Error('Media destroyed during unlock'), nextSpeech);
                  return;
                }
                
                try {
                  playMedia(nextSpeech);
                } catch (playError) {
                  console.error('Error playing media after unlock:', playError);
                  safeOnError(playError, nextSpeech);
                }
              });
            } else {
              // No media object to unlock, call error immediately
              clearTimeout(unlockTimeoutId);
              safeOnError(error, nextSpeech);
            }
          } else {
            // Already attempted unlock - fail immediately to prevent loop
            // This can happen if:
            // 1. The unlock succeeded but playMedia failed again (not an autoplay issue)
            // 2. A non-autoplay error (network, codec, corrupt file, etc.)
            console.error('Play error after unlock attempt, failing immediately to prevent loop');
            safeOnError(error, nextSpeech);
          }
        },
        onload: () => {
          try {
            // Audio loaded successfully - get actual duration and reset timeout
            const audioDurationSeconds = media.duration();
            const audioDurationMs = audioDurationSeconds * 1000;
            
            // Validation: duration should be positive and reasonable
            // If duration is 0 or unreasonably small, use a safe fallback
            // This can happen due to browser/codec issues, mobile device quirks,
            // or when audio metadata isn't fully parsed when onload fires
            const MIN_REASONABLE_DURATION_MS = 200; // 0.2 seconds
            const FALLBACK_DURATION_MS = 30000; // 30 seconds fallback if duration unknown
            
            let playbackTimeoutMs;
            if (audioDurationMs < MIN_REASONABLE_DURATION_MS) {
              console.warn(`Audio duration unreliable (${audioDurationSeconds}s), using fallback timeout`);
              playbackTimeoutMs = FALLBACK_DURATION_MS;
            } else {
              playbackTimeoutMs = audioDurationMs + PLAYBACK_BUFFER_MS;
            }
                        
            // Reset timeout based on actual audio duration
            setTimeoutForDuration(playbackTimeoutMs, 'playback');
            
            playMedia(nextSpeech);
          } catch (playError) {
            console.error('Error in playMedia:', playError);
            safeOnError(playError, nextSpeech);
          }
        },
        onplay: () => {
          setOnSpeakingTimer();
          onPlay(nextSpeech);
        },
        onstop: () => {
          const audioStopped = true;
          setOnSpeakingTimer(audioStopped);
          safeOnEnd(nextSpeech);
        },
        onend: () => {
          const audioStopped = true;
          setOnSpeakingTimer(audioStopped);
          safeOnEnd(nextSpeech);
        }
      });
    }catch(e){
      console.error("Error creating new Howl", e)
      logSentryException(new Error('Howl audio creation failure'), 'Error creating new Howl', { originalError: e?.message, originalStack: e?.stack });
      // Call onError to ensure callbacks fire even when Howl construction fails
      // Note: safeOnError will clear audioTimeoutId via ensureCallbackOnce
      safeOnError(e, nextSpeech);
    }

      nextSpeech.media = media;
  }

  /** home app section */
  abortDance(){
    // Skip WebGL operations if WebGL failed
    if (this.props.webglError) {
      return;
    }

    if (amiraAvatar.spineContent && amiraAvatar.spineContent.state) {
      amiraAvatar.spineContent.state.setAnimation(3, "gestures/Idle", true);
      this.props.webglProps.resetAmiraAnimations();
    }
  }
  /** home app section end */

  /** home app section */
  playDance() {
    // Skip dance animations if WebGL failed
    if (this.props.webglError) {
      console.log('WebGL error detected, skipping dance animation');
      return;
    }

    try {
      let dance = this.props.avatarSpeechProps.dance;

      let playDanceGesture = (onDanceEnd) => {
        if (!amiraAvatar.spineContent || !amiraAvatar.spineContent.state) {
          console.warn('Cannot play dance gesture: amiraAvatar.spineContent not initialized');
          return null;
        }

        const trackEntry = amiraAvatar.spineContent.state.setAnimation(3, "gestures/" + dance.gesture, false);
        const durationMs = Math.floor(trackEntry.animation.duration * 1000);
        if(this.props.avatarSpeechProps.onDanceStart){
          this.props.avatarSpeechProps.onDanceStart();
        }
        if(onDanceEnd){
          setTimeout(()=> {
            onDanceEnd();
            // Return to idle animation when dance completes
            if (amiraAvatar.spineContent && amiraAvatar.spineContent.state) {
              amiraAvatar.spineContent.state.setAnimation(3, "gestures/Idle", true);
            }
          }, durationMs)
        } else {
          // If no onDanceEnd callback, still return to idle
          setTimeout(()=> {
            if (amiraAvatar.spineContent && amiraAvatar.spineContent.state) {
              amiraAvatar.spineContent.state.setAnimation(3, "gestures/Idle", true);
            }
          }, durationMs)
        }
        return trackEntry;
      }

      this.props.webglProps.sendAmiraGestureArray(gestureArray);
      if(dance.audioUrl) {
        this.props.speech.playAppSounds({
          url: dance.audioUrl,
          onfinish: this.onFinishedPlaying.bind(this),
          onStart: e => playDanceGesture(this.props.avatarSpeechProps.onDanceEnd),
          volume: '0.10',
          key: gameSFXKeys.danceMusic,
        }); // renegade is a bit longer, keep default audioTimeoutDelay
      } else {
        // IF we have no audioUrl that means we only want to play the animation/gesture
        // so no need to call `onFinishedPlaying` since we have no media to release.
        return playDanceGesture(this.props.avatarSpeechProps.onDanceEnd);
      }
    } catch (e) {
      this.abortDance();
      console.log(`Error playing dance: ${this.props.avatarSpeechProps.dance.gesture}`, e);
      logSessionInfo('amira_dance_failure', {
        error: e,
        avatarSpeechProps: this.props.avatarSpeechProps,
      });
    }
  }
  /** home app section end */

  /** home app section */
  onWordEnd(word) {
    if (this.props.speech && this.props.avatarSpeechProps.onBoundary) {
      this.props.avatarSpeechProps.onBoundary(word);
    }
  }
  /** home app section end */

  /** home app section */
  onSpeaking(position) {
    let index = 0;
    let aSpeechMarks = this.props.avatarSpeechProps.speech_marks;

    if (aSpeechMarks && position) {
      aSpeechMarks.forEach(sentence => {
        sentence.words.forEach(word => {

          if (word.value && word.time && (word.time <= position) && !(word.value.includes("<") && word.value.includes(">"))) {
            word.start = index; //Override the start character with the word index
            this.onWordEnd(word);
            word.time = null;    //Do this so the word isn't evaluated again
            if (mounted) {
              this.setState({ speech_marks: aSpeechMarks });
            }
          }
          if (word.value && !(word.value.includes("<") && word.value.includes(">"))) index++;
        });
      });
    }
  }
  /** home app section end */

  /** home app section */
  onUtteranceStart(speechObj) {
    if (speechObj && speechObj.onStart && !speechObj.aborted) {
      speechObj.onStart();
    }
  }
  /** home app section end */

  async abortSpeech(abortCallback) {
    prepareMicForRecord();
    this.props.speech.restartRecorderStreamIfNeeded();

    //console.log('COACH- abort speech');
    for (const s of speechPropsArray) {
      s.aborted = true;
      if(s.media && s.playing) {
        this.destroyMedia();
      }
      await this.onAbortPlaying(s);
    }

    if(speaking){
      if (!this.props.isHidden && !this.props.webglError) {
        visemeArray = [];
        gestureArray = [];
        this.props.webglProps.sendAmiraVisemeArray(visemeArray);
        this.props.webglProps.sendAmiraGestureArray(gestureArray);
        this.props.webglProps.setAmiraPlayAnimations(false);
        this.props.webglProps.resetAmiraAnimations();
      }
      speaking = false;
    }

    abortCallback && await abortCallback();
  }

  speak(message, skipAvatar) {
    let props = {};
    props.textToSpeak = message.pronunciation;

    if (!skipAvatar) {
      let textToSpeak = message.pronunciation || "";
      textToSpeak = genSpeechGestures(textToSpeak, "en") || "";
      //LEAVE IN FOR DEBUG console.log("Speaking:",textToSpeak);
      if (!textToSpeak.includes("<speak>")) {
        textToSpeak = "<speak>" + textToSpeak + "</speak>";
      }

      props.textToSpeak = textToSpeak;
    }

    if (skipAvatar || (this.props.config.useSumerian == false)) props.skipAvatar = skipAvatar;

    //if we have a callback for when the message is done, add it to onEnd
    if(message.callback){
      props.onEnd = message.callback;
    }

    if(message.passThroughSpeechTags) props.passThroughSpeechTags = true;
    this.props.speech.speak(props);
  }

  onReceiveMessage(evt){
    const dataType = {
     SUMERIANREADY: "SumerianReady",
     SPEECHDONE: "SpeechDone",
    };

    if(evt.data == dataType.SUMERIANREADY){
      this.props.setCoachReady(true);
    }
    //Note: IntroSpeechDone
    if(evt.data == dataType.SPEECHDONE && this.props.avatarSpeechProps.onEnd){
      this.props.avatarSpeechProps.onEnd();
    }
    //TODO: Add assessment ready when we bring that in.
  }

  async onAbortPlaying(speechObj) {
    // console.log('COACH, onAbortPlaying', speechObj);
    if(timer){
      clearInterval(timer);
    }

    if(!speechObj) return;
    const { resolveAbort } = speechObj;

    if (resolveAbort) {
      resolveAbort();
    } else {
      // only if speakWithPromise is used and no resolveAbort function is found
      console.warn("No resolve abort function found, something could be stuck if speakWithPromise is used.");
    }
    speechPropsArray = speechPropsArray.filter(s => s.id != speechObj.id);
  }

  async onFinishedPlaying(speechObj) {
    //console.log('COACH, onFinishedPlaying', speech);
    if(timer){
      clearInterval(timer);
    }
    if (speechObj && !speechObj.aborted) {
      this.speechEndCallback(speechObj);
    }
    if (speechObj) {
      speechPropsArray = speechPropsArray.filter(s => s.id != speechObj.id);
    }
  }

  handleContextRestored = (event) => {
    // Guard against execution on unmounted component
    if (!this._isMounted) {
      return;
    }
    
    logSessionInfo('webgl_context_restored', {
      texturesRestored: event.detail?.texturesRestored,
      errors: event.detail?.errors,
      timestamp: Date.now()
    });
    
    // Validate that Amira textures are still valid after context restore
    if (amiraAvatar && amiraAvatar.spineContent && amiraAvatar.spineContent.skeleton) {
      const invalidSlots = this.validateSlotTextures(amiraAvatar.spineContent.skeleton);
      
      // Only re-render if validation found issues and component is still mounted
      if (invalidSlots.length > 0 && !this.props.isHidden && !this.props.webglError && this._isMounted) {
        console.warn('Re-rendering Amira due to invalid textures after context restore');
        setTimeout(() => {
          if (this._isMounted) {
            this.renderAmira();
          }
        }, 500);
      }
    }
  };

  handleTextureRestorationErrors = (event) => {
    if (!this._isMounted) return;
    console.error('Texture restoration errors', event.detail);
    logSessionInfo('texture_restoration_errors', event.detail);
  };

  handleAtlasValidationErrors = (event) => {
    if (!this._isMounted) return;
    console.error('Atlas validation errors', event.detail);
    logSessionInfo('atlas_validation_errors', event.detail);
  };

  handleTextureDimensionMismatch = (event) => {
    if (!this._isMounted) return;
    console.error('Texture dimension mismatch', event.detail);
    logSessionInfo('texture_dimension_mismatch', event.detail);
  };

  handleTextureLoadError = (event) => {
    if (!this._isMounted) return;
    console.error('Texture load error', event.detail);
    logSessionInfo('texture_load_error', event.detail);
  };

  handleInvalidSlotTextures = (event) => {
    if (!this._isMounted) return;
    console.error('Invalid slot textures', event.detail);
    logSessionInfo('invalid_slot_textures', event.detail);
  };

  handleAtlasPagesInvalid = (event) => {
    if (!this._isMounted) return;
    console.error('Atlas pages invalid', event.detail);
    logSessionInfo('atlas_pages_invalid', event.detail);
  };

  handleTestTriggerRender = (event) => {
    if (!this._isMounted) return;
    if (!this.props.webglError && !this.props.isHidden) {
      this.renderAmira();
    }
  };

  loadVisemes() {
    visemeArray = [];
    for (const sentence of animationJson) {
      for (const word of sentence.words) {
        if (word.type === 'word') {
          for (const viseme of word.visemes) {
            visemeArray.push(viseme);
          }
        }
      }
    }
  }

  /** home app section */
  loadGestures() {
    gestureArray = [];
    for (const sentence of animationJson) {
      for (const word of sentence.words) {
        if (word.type === 'ssml') {
          let requestedGesture = word.value.replace("gesture:", "");
          if(requestedGesture != amiraGestures.SweepWave) {
            requestedGesture = amiraGestures[requestedGesture] || amiraGestures.Idle;
            word.value = "gestures/" + requestedGesture;
            if(word) {
              gestureArray.push(word);
            }
          }
        }
      }
    }
  }
  /** home app section end */

  render() {
    // webglError is the single source of truth for error state
    // It covers both initialization failures and render failures after max retries
    // When set, triggers StaticAmiraFallback and rightPanel.webgl-error CSS class
    const shouldShowFallback = this.props.webglError;
    
    return (
      <div className="coachContainer">
        <div className="coach">
          {this.props.isCoachReady && this.props.isAppReady && (
            <ChatBot
              speech={this.props.speech}
              transcript={!this.props.isReaderActive ? this.props.transcript : {}} //Only pass transcript when Reader is not listening
              isReaderActive={this.props.isReaderActive}
              muteReader={this.props.muteReader}
              unMuteReader={this.props.unMuteReader} />
          )}
        </div>
        {/* Render static fallback when WebGL fails (either init or render after retries) */}
        {shouldShowFallback && (
          <StaticAmiraFallback isHidden={this.props.isHidden} />
        )}
      </div>
    );
  }
}

export default withRouter(Coach);
